import React, { useCallback, useEffect, useRef, useState } from "react";
import { radStudyApi, radReportApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Modal, Button, Badge, Section, Field, Tabs, Loading, ReasonModal } from "../../Components/UI/ui";
import {
  GATE, RAD_STATUS_LABEL, REPORT_SECTIONS, MAX_KEY_IMAGES, DICOM_MAX_BYTES,
  fmtDateTime, modalityLabel, humanSize, openPdf, deliveryLine, cap, ohifStudyUrl,
} from "../../lib/lab";
import AcquireModal from "./AcquireModal";

// One radiology study end to end (radiology doc §3): mark performed, upload
// DICOM, view images, write the six sections, verify, publish, add an addendum.
const StudyDetail = ({ id, open, onClose, onChanged, rooms = [] }) => {
  const { can } = useAuth();
  const { toast, defaultLayout, loadBranding } = useApp();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("report");
  const [report, setReport] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [series, setSeries] = useState([]);
  const [upload, setUpload] = useState(null);     // { name, pct }
  const [acquireOpen, setAcquireOpen] = useState(false);
  const [addendumOpen, setAddendumOpen] = useState(false);
  const [addendumText, setAddendumText] = useState("");
  const [unverifyOpen, setUnverifyOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const fileRef = useRef(null);
  const onChangedRef = useRef(onChanged); onChangedRef.current = onChanged;
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;

  const hydrate = (study) => {
    setS(study);
    setReport({ ...(study.report || {}) });
    setDirty(false);
  };

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const study = await radStudyApi.get(id);
      hydrate(study);
      if (study.dicom?.studyInstanceUID) {
        try { setSeries((await radStudyApi.series(id))?.series || []); } catch { setSeries([]); }
      } else setSeries([]);
    } catch (e) { toast.error(e); onCloseRef.current?.(); }
    finally { setLoading(false); }
  }, [id, toast]);

  useEffect(() => { if (open) { load(); loadBranding(); } }, [open, load, loadBranding]);

  const changed = (study) => { hydrate(study); onChangedRef.current?.(study); };

  const canScan = can(...GATE.acquireScan);
  const canReport = can(...GATE.reportStudy);
  const status = s?.status;
  const editable = canReport && ["ACQUIRED", "DRAFT"].includes(status);

  // `force` sends all six sections even when nothing was edited — that is what
  // moves an untouched ACQUIRED study to DRAFT, which verify requires.
  const saveReport = async ({ force = false } = {}) => {
    setBusy("save"); setErr(null);
    try {
      const body = {};
      for (const { key } of REPORT_SECTIONS) if (force || (report[key] ?? "") !== (s.report?.[key] ?? "")) body[key] = report[key] ?? "";
      if (!Object.keys(body).length) { setDirty(false); return true; }
      changed(await radStudyApi.saveReport(s._id, body));
      toast.success("Report saved");
      return true;
    } catch (e) { setErr(apiError(e)); return false; }
    finally { setBusy(null); }
  };

  const verify = async () => {
    // The server verifies a DRAFT only. Sections are prefilled from the
    // template, so a radiologist who agrees with them clicks Verify without
    // editing anything — save first so the study leaves ACQUIRED.
    if (dirty || status === "ACQUIRED") {
      if (!(await saveReport({ force: status === "ACQUIRED" }))) return;
    }
    setBusy("verify"); setErr(null);
    try { changed(await radStudyApi.verify(s._id)); toast.success("Verified"); }
    catch (e) { setErr(apiError(e)); } finally { setBusy(null); }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      const res = await radStudyApi.publish(s._id);
      changed(res.study || res);
      toast.success(res.pendingStudies?.length
        ? `Published · ${res.pendingStudies.length} scan${res.pendingStudies.length === 1 ? "" : "s"} still to report on this booking`
        : "Published · report is with the patient");
      if (res.attachError) toast.error(`The PDF could not be attached (${res.attachError}). Use "Retry PDF".`);
      const d = res.reportDelivery;
      if (d?.status === "sent") toast.success(deliveryLine(d));
      else if (d?.status === "failed") toast.error(`${deliveryLine(d)} — use "Resend".`);
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const addAddendum = async () => {
    if (!addendumText.trim()) return;
    setBusy("addendum");
    try {
      const res = await radStudyApi.addendum(s._id, addendumText.trim());
      changed(res.study || res);
      setAddendumOpen(false); setAddendumText("");
      toast.success(`Addendum added · revision ${res.revision}`);
      if (res.attachError) toast.error(`The PDF could not be regenerated (${res.attachError}).`);
      else toast.info("The patient is not notified automatically — use Send to share the updated report.");
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const pickFile = async (file) => {
    if (!file) return;
    if (file.size > DICOM_MAX_BYTES) {
      toast.error(`${file.name} is ${humanSize(file.size)} — the limit is ${humanSize(DICOM_MAX_BYTES)} per upload. Send the study one series at a time.`);
      return;
    }
    setUpload({ name: file.name, pct: 0 });
    try {
      const res = await radStudyApi.uploadDicom(s._id, file, (pct) => setUpload((u) => (u ? { ...u, pct } : u)));
      const bits = [`${res.instanceCount ?? 0} image${res.instanceCount === 1 ? "" : "s"} in this study`];
      if (res.skipped) bits.push(`${res.skipped} file${res.skipped === 1 ? "" : "s"} skipped (not DICOM)`);
      toast.success(bits.join(" · "));
      if (res.otherStudyUIDs?.length) toast.error("That archive held more than one DICOM study — only the first was linked. Upload one study at a time.");
      await load();
    } catch (e) { toast.error(e); }
    finally { setUpload(null); if (fileRef.current) fileRef.current.value = ""; }
  };

  const toggleKeyImage = async ({ sopInstanceUID, seriesInstanceUID }) => {
    const existing = s.dicom?.keyImages || [];
    const has = existing.some((k) => k.sopInstanceUID === sopInstanceUID);
    if (!has && existing.length >= MAX_KEY_IMAGES) return toast.error(`Up to ${MAX_KEY_IMAGES} key images print on the report — remove one first.`);
    const next = has
      ? existing.filter((k) => k.sopInstanceUID !== sopInstanceUID)
      : [...existing, { sopInstanceUID, seriesInstanceUID, caption: "" }];
    setBusy("keyImages");
    try {
      changed(await radStudyApi.setKeyImages(s._id, next.map(({ sopInstanceUID: u, seriesInstanceUID: v, caption }) => ({ sopInstanceUID: u, seriesInstanceUID: v, caption: caption || "" }))));
      toast.success(has ? "Key image removed" : "Key image added");
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const setCaption = async (sop, caption) => {
    const next = (s.dicom?.keyImages || []).map((k) => (k.sopInstanceUID === sop ? { ...k, caption } : k));
    try { changed(await radStudyApi.setKeyImages(s._id, next)); } catch (e) { toast.error(e); }
  };

  const retryPdf = async () => {
    setBusy("pdf");
    try { await radReportApi.attach(s._id); toast.success("PDF regenerated and attached"); load(); }
    catch (e) { toast.error(e); } finally { setBusy(null); }
  };
  const sendReport = async () => {
    setBusy("send");
    try { const r = await radReportApi.send(s._id); toast.success(r?.delivery ? deliveryLine(r.delivery) : r?.message || "Report sent"); }
    catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const keyUIDs = (s?.dicom?.keyImages || []).map((k) => k.sopInstanceUID);
  const viewerUrl = ohifStudyUrl(s?.dicom?.studyInstanceUID);
  const snap = s?.templateSnapshot || {};

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="lg"
        title={s ? `${snap.name || modalityLabel(s.modality)} · ${s.patientName}` : "Study"}
        footer={s && (
          <>
            {can(...GATE.cancelStudy) && !["PUBLISHED", "CANCELLED"].includes(status) && (
              <Button size="sm" variant="danger" onClick={() => setCancelOpen(true)} style={{ marginRight: "auto" }}>Cancel study</Button>
            )}
            {status === "PENDING" && canScan && <Button variant="primary" onClick={() => setAcquireOpen(true)}>Scan performed</Button>}
            {editable && <Button variant={dirty ? "navy" : "secondary"} loading={busy === "save"} onClick={() => saveReport()}>Save report</Button>}
            {canReport && ["ACQUIRED", "DRAFT"].includes(status) && <Button variant="navy" loading={busy === "verify"} onClick={verify}>Verify</Button>}
            {canReport && status === "VERIFIED" && (
              <>
                <Button onClick={() => setUnverifyOpen(true)}>Unverify</Button>
                <Button variant="success" loading={busy === "publish"} onClick={publish}>Publish report</Button>
              </>
            )}
            {status === "PUBLISHED" && (
              <>
                {canReport && <Button onClick={() => setAddendumOpen(true)}>Add addendum</Button>}
                <Button size="sm" onClick={() => openPdf(radReportApi.pdfUrl(s._id, { layout: defaultLayout }))}>🖨 Print</Button>
                {can(...GATE.sendReport) && <Button size="sm" loading={busy === "send"} onClick={sendReport}>Send on WhatsApp</Button>}
              </>
            )}
          </>
        )}
      >
        {loading || !s ? <Loading /> : (
          <>
            <div className="lab-row between">
              <div className="lab-row">
                <Badge kind={status}>{RAD_STATUS_LABEL[status] || status}</Badge>
                <Badge kind="radiology">{modalityLabel(s.modality)}</Badge>
                {s.bodyPart && <span className="lab-muted">{s.bodyPart}</span>}
                {s.laterality && <Badge kind="muted">{s.laterality}</Badge>}
                {s.accession && <span className="lab-sample-id" style={{ fontSize: 14 }}>{s.accession}</span>}
                {s.revision > 1 && <Badge kind="IN_PROCESS">Revision {s.revision}</Badge>}
              </div>
              <div className="lab-muted">{s.appointment?.scheduledAt ? fmtDateTime(s.appointment.scheduledAt) : ""}</div>
            </div>

            {s.contrast?.used && <div className="lab-muted">Contrast: {s.contrast.agent || "given"}{s.contrast.dose ? ` · ${s.contrast.dose}` : ""}{s.contrast.route ? ` · ${s.contrast.route}` : ""}</div>}
            {s.safetyNotes && <div className="lab-muted">Safety: {s.safetyNotes}</div>}
            {status === "PENDING" && <div className="lab-muted">The scan has not been marked performed yet — images and the report open up after that.</div>}
            {err && <div className="lab-danger-text">{err}</div>}

            <Tabs value={tab} onChange={setTab} tabs={[
              { id: "report", label: "Report" },
              { id: "images", label: "Images", count: s.dicom?.instanceCount || undefined },
              { id: "about", label: "Details" },
            ]} />

            {tab === "report" && (
              <>
                {REPORT_SECTIONS.map(({ key, label, rows, required }) => (
                  <Field key={key} label={<>{label}{required && <span className="req" style={{ color: "var(--red)" }}> *</span>}</>}>
                    <textarea
                      className="lab-textarea w-full"
                      rows={rows}
                      value={report[key] ?? ""}
                      disabled={!editable}
                      onChange={(e) => { setReport({ ...report, [key]: e.target.value }); setDirty(true); }}
                    />
                  </Field>
                ))}
                {!editable && status !== "PENDING" && (
                  <div className="lab-muted">
                    {status === "PUBLISHED" ? "A published report is never edited — add an addendum instead." : status === "VERIFIED" ? "Verified. Unverify to make a change." : "Only a signatory can write the report."}
                  </div>
                )}
                {(s.addenda || []).length > 0 && (
                  <Section title="Addenda">
                    {s.addenda.map((a) => (
                      <div key={a.revision} style={{ borderLeft: "4px solid var(--yellow)", paddingLeft: 12, marginBottom: 10 }}>
                        <div className="lab-muted" style={{ fontSize: 12 }}>Revision {a.revision} · {fmtDateTime(a.at)} · {a.by?.name || "—"}</div>
                        <div>{a.text}</div>
                      </div>
                    ))}
                  </Section>
                )}
                {s.reportedBy?.name && (
                  <div className="lab-muted">
                    Reported by {s.reportedBy.name}{s.reportedBy.qualification ? `, ${s.reportedBy.qualification}` : ""}{s.reportedBy.regNo ? ` · Reg. ${s.reportedBy.regNo}` : ""}
                  </div>
                )}
              </>
            )}

            {tab === "images" && (
              <>
                {canScan && status !== "PUBLISHED" && status !== "CANCELLED" && (
                  <div className="lab-row" style={{ gap: 10, flexWrap: "wrap" }}>
                    <input ref={fileRef} type="file" accept=".dcm,.zip,application/dicom,application/zip" className="lab-input" style={{ paddingTop: 7, flex: 1, minWidth: 260 }}
                      disabled={status === "PENDING" || !!upload} onChange={(e) => pickFile(e.target.files?.[0])} />
                    <span className="lab-muted" style={{ fontSize: 12 }}>
                      {status === "PENDING" ? "Mark the scan performed first" : `One .dcm or a .zip per series · up to ${humanSize(DICOM_MAX_BYTES)}`}
                    </span>
                  </div>
                )}
                {upload && (
                  <div>
                    <div className="lab-row between" style={{ fontSize: 13 }}>
                      <span>{upload.name}</span><span>{upload.pct}%</span>
                    </div>
                    <div style={{ height: 8, background: "#edf3f7", borderRadius: 999, overflow: "hidden", marginTop: 4 }}>
                      <div style={{ width: `${upload.pct}%`, height: "100%", background: "var(--teal)", transition: "width .2s" }} />
                    </div>
                    <div className="lab-muted" style={{ fontSize: 12, marginTop: 4 }}>Keep this open until the upload finishes.</div>
                  </div>
                )}

                {!s.dicom?.studyInstanceUID ? (
                  <div className="lab-muted">No images uploaded for this scan yet.</div>
                ) : (
                  <>
                    <div className="lab-row between" style={{ background: "var(--ground, #f5f8f9)", borderRadius: 10, padding: "12px 16px" }}>
                      <span>
                        <b>{s.dicom.instanceCount || 0} image{s.dicom.instanceCount === 1 ? "" : "s"}</b>
                        <span className="lab-muted"> in {series.length || 1} series</span>
                      </span>
                      {viewerUrl
                        ? <Button size="sm" variant="navy" onClick={() => window.open(viewerUrl, "_blank", "noopener")}>Open in viewer ↗</Button>
                        : <span className="lab-muted" style={{ fontSize: 12 }}>Image viewer URL is not configured for this environment.</span>}
                    </div>

                    {canReport && status !== "PUBLISHED" && (
                      <Section title={`Key images · ${(s.dicom.keyImages || []).length} of ${MAX_KEY_IMAGES}`}>
                        <div className="lab-muted" style={{ marginBottom: 10 }}>
                          These, and only these, print on the report. Open the study in the viewer, note the image number, then tick it here.
                        </div>
                        {series.map((ser, si) => (
                          <div key={ser.seriesInstanceUID} style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                              Series {si + 1}{ser.modality ? ` · ${ser.modality}` : ""}{ser.description ? ` · ${ser.description}` : ""}
                              <span className="lab-muted" style={{ fontWeight: 400 }}> · {(ser.instances || []).length} images</span>
                            </div>
                            <div className="lab-row" style={{ gap: 6, flexWrap: "wrap" }}>
                              {(ser.instances || []).map((sop, i) => {
                                const on = keyUIDs.includes(sop);
                                return (
                                  <Button key={sop} size="xs" variant={on ? "success" : "secondary"} loading={busy === "keyImages"} title={sop}
                                    onClick={() => toggleKeyImage({ sopInstanceUID: sop, seriesInstanceUID: ser.seriesInstanceUID })}>
                                    {on ? "✓ " : ""}{i + 1}
                                  </Button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        {!series.length && <span className="lab-muted">Series are still being read back from the store — reopen this tab in a moment.</span>}
                      </Section>
                    )}

                    {(s.dicom.keyImages || []).length > 0 && (
                      <Section title={`Chosen key images · ${s.dicom.keyImages.length}`}>
                        {s.dicom.keyImages.map((k) => {
                          const ser = series.find((x) => x.seriesInstanceUID === k.seriesInstanceUID);
                          const idx = ser ? (ser.instances || []).indexOf(k.sopInstanceUID) : -1;
                          return (
                            <div className="lab-row" key={k.sopInstanceUID} style={{ marginBottom: 6 }}>
                              <span className="lab-tag" style={{ minWidth: 92 }}>{idx >= 0 ? `Image ${idx + 1}` : "Image"}</span>
                              <input className="lab-input sm" style={{ flex: 1 }} placeholder="Caption, e.g. PA view" defaultValue={k.caption || ""}
                                disabled={!canReport || status === "PUBLISHED"} onBlur={(e) => e.target.value !== (k.caption || "") && setCaption(k.sopInstanceUID, e.target.value)} />
                              {canReport && status !== "PUBLISHED" && (
                                <Button size="xs" variant="ghost" loading={busy === "keyImages"} onClick={() => toggleKeyImage({ sopInstanceUID: k.sopInstanceUID, seriesInstanceUID: k.seriesInstanceUID })}>Remove</Button>
                              )}
                            </div>
                          );
                        })}
                      </Section>
                    )}
                  </>
                )}
              </>
            )}

            {tab === "about" && (
              <>
                <Section title="Study">
                  <dl className="lab-kv">
                    <dt>Patient</dt><dd>{s.patientName}{s.appointment?.patientPhone ? ` · ${s.appointment.patientPhone}` : ""}</dd>
                    <dt>Procedure</dt><dd>{snap.name || "—"}{snap.views?.length ? ` · ${snap.views.join(", ")}` : ""}</dd>
                    <dt>Accession</dt><dd>{s.accession || <span className="lab-muted">assigned when the scan is performed</span>}</dd>
                    <dt>Room</dt><dd>{s.room || "—"}</dd>
                    <dt>Scan done</dt><dd>{s.acquiredAt ? fmtDateTime(s.acquiredAt) : "—"}{s.radiographer?.name ? ` · ${s.radiographer.name}` : ""}</dd>
                    <dt>Verified</dt><dd>{s.verifiedAt ? fmtDateTime(s.verifiedAt) : "—"}</dd>
                    <dt>Published</dt><dd>{s.publishedAt ? fmtDateTime(s.publishedAt) : "—"}</dd>
                    {s.appointment?.referral?.doctorName && (<><dt>Referred by</dt><dd>{s.appointment.referral.doctorName}</dd></>)}
                    <dt>Images</dt><dd>{s.dicom?.instanceCount ? `${s.dicom.instanceCount} in ${series.length || 1} series` : "none uploaded"}</dd>
                  </dl>
                  {status === "PUBLISHED" && (
                    <div className="lab-row" style={{ marginTop: 10 }}>
                      <Button size="sm" variant="ghost" loading={busy === "pdf"} onClick={retryPdf} title="Regenerate the stored PDF, e.g. after a letterhead change">Regenerate PDF</Button>
                    </div>
                  )}
                </Section>
                <Section title="History">
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                    {[...(s.history || [])].reverse().map((h, i) => (
                      <div key={i} className="lab-row">
                        <Badge kind={h.action}>{cap(h.action)}</Badge>
                        <span className="lab-muted">{fmtDateTime(h.at)} · {h.byName || "—"}{h.note ? ` · ${h.note}` : ""}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              </>
            )}
          </>
        )}
      </Modal>

      <AcquireModal open={acquireOpen} onClose={() => setAcquireOpen(false)} study={s} rooms={rooms} onDone={() => load()} />

      <Modal open={addendumOpen} onClose={() => setAddendumOpen(false)} size="sm" title="Add an addendum"
        footer={<><Button onClick={() => setAddendumOpen(false)}>Cancel</Button><Button variant="primary" loading={busy === "addendum"} disabled={!addendumText.trim()} onClick={addAddendum}>Add addendum</Button></>}>
        <div className="lab-muted">The published report is never changed. This appends a dated, signed note, bumps the revision and regenerates the PDF.</div>
        <Field label="Addendum"><textarea className="lab-textarea w-full" rows={5} value={addendumText} autoFocus onChange={(e) => setAddendumText(e.target.value)} /></Field>
      </Modal>

      <ReasonModal open={unverifyOpen} title="Unverify report" label="Why is it being reopened?" onClose={() => setUnverifyOpen(false)} busy={!!busy}
        onSubmit={async (reason) => { setBusy("unverify"); try { changed(await radStudyApi.unverify(s._id, reason)); toast.success("Reopened for editing"); setUnverifyOpen(false); } catch (e) { toast.error(e); } finally { setBusy(null); } }} />

      <ReasonModal open={cancelOpen} title="Cancel study" onClose={() => setCancelOpen(false)} busy={!!busy}
        onSubmit={async (reason) => { setBusy("cancel"); try { changed(await radStudyApi.cancel(s._id, reason)); toast.success("Study cancelled"); setCancelOpen(false); } catch (e) { toast.error(e); } finally { setBusy(null); } }} />
    </>
  );
};

export default StudyDetail;
